import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
// store
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { v4 as uuid4 } from 'uuid';

// services
import { ErrorHandler } from '../../shared/services/error-handler.service';
import { DataStorageService } from '../../shared/services/data-storage.service';
import { SharedService } from '../../shared/services/shared-service.service';
import { LoginService } from '../../shared/services/login.service';
import { AbandonmentService } from '../../shared/services/abandonment.service';
import { AppLoaderService } from '../../shared/services/app-loader.service';
import { BagItemsService } from '../../shared/services/bagItems.service';
import { EventsService } from './events.service';

// models
import { AppState } from '../models/app-state.interface';
import { BookingState, BookingJourneyItem, BookingFlightData, BookerItem, GenerateBookingReference } from '../models/booking.interface';
import { TimeslotBookingObject } from '../models/timeslot-reservation.interface';
import { CollectionLocation } from '../models/collection.interface';
import { DeliveryLocation } from '../models/delivery.interface';
import { BillingAddress, MakePaymentRequest } from '../models/payment.interface';
import { PaymentRequest } from '../models/payment-request.interface';
import { AmendRequest } from '../models/amend.interface';
// init models
import { initialBooking, initialBookingJourney, initialBookingFlightData } from '../models/booking-init.model';
import { initialMakePaymentRequest } from '../models/make-payment-init.model';
import { initialCollectionLocation, initialDeliveryLocation } from '../models/location-init.model';
// globals
import {
  API_PATH,
  SUBSCRIPTION_KEY,
  ADD_BOOKING,
  MAKE_PAYMENT,
  AMEND_BOOKING,
  GENERATE_BOOKING_REFERENCE,
  VALIDATE_PHONE_NUMBER,
  ISO_CURRENCY_CODES,
  GET_APPLE_PAY_SESSION,
  SLOT_NUMBER,
  GET_MAM_ACCESS_TOKEN,
  CHECK_FLY_BACK_ELIGIBILITY
} from '../../shared/globals';
import { DEFAULT_INTERPOLATION_CONFIG } from '@angular/compiler';
import moment from 'moment';
import { bookerDataBackup, saveUuid, updateAddBookingCalled, updateBagItems, updateBookingAmendPaymentFailed, updateBookingJourneyReference, updateBookingReference, updateBookingReferenceState, updatePnrData, updateUniqueSequenceId, updateVirtualBags } from '../reducers/booking.actions';
import { applePayToken, googlePayToken, paymentWithSavedCard, saveBookingDetails, updateAddBookingCount, updateInboundBookingObject, updateOutboundBookingObject } from '../reducers/d2d-booking.reducer';
import { AIRPORTS_LOADED, CURRENT_BOOKING_LOADED } from '@shared/reducers/loading-status.reducer';
import { UPDATE_USER_EMAIL } from '@shared/reducers/user.reducer';
import { updateManualDestination } from '../reducers/flight.reducer';
import { updateAmendObject, updateBookingObject } from '../reducers/current-booking.reducer';
import { UPDATE_USER_AGENT_BOOKING_REFERENCE } from '@shared/reducers/user-agent.reducer';
import { updateFlybackBookingStatus, updateFlyBackCompleteStatus, updateFlybackEligibleProducts } from '../reducers/flyback.reducer';
import { amendPaymentConfirmed } from '../reducers/price.reducer';
import { update3dsData } from '../reducers/threeDS2.reducer';
import { isBookingHubdropBooking, setSelectedPostOffice } from '@shared/reducers/utility.actions';
import { saveD2DBookingsData } from '../reducers/old-booking-info.reducer';
import { updateReturnCustomerUrl } from '@shared/reducers/flight-finder.action';
// GA script
declare var ga: any;
declare var exponea: any;
declare var ThreedDS2Utils: any;
declare var libphonenumber: any;

const AMEND_MAPPING = {
  64: '',
  56: 'Bag, Location, Timeslot',
  48: 'Location, Timeslot',
  40: 'Bag, Location',
  24: 'Bag, Timeslot',
  16: 'Timeslot',
  8: 'Bag',
}

const eligibilityProducts = {
  0: 'None',
  2: 'ABC',
  3: 'CCD'
}

@Injectable({
  providedIn: 'root',
})
export class BookingService {

  public dataLinkVariableUpdated: Subject<boolean> = new Subject();
  public bookingInitFinished = false;
  public completeBookingRef = '';
  public generateBooking$: Subject<any> = new Subject();
  public bookingPaymentError$: Subject<any> = new Subject();
  public manualFlightDataStored$: Subject<any> = new Subject();
  public getAllAirports$: Subject<any> = new BehaviorSubject<any>(null);
  public bookingStored$: Subject<Boolean> = new BehaviorSubject<Boolean>(false);
  public validPhoneNumberSubject$: Subject<any> = new Subject();
  public upsellBookingAdded$: Subject<any> = new Subject();
  public applePaymentComplete$: Subject<any> = new Subject();

  public currentBooking: BookingState = initialBooking;
  private _bookingOriginal = Object.assign({}, this.currentBooking);

  private _currentBookingJourney: BookingJourneyItem = initialBookingJourney;
  private _bookingJourneyOriginal = Object.assign({}, this._currentBookingJourney);

  private _currentBookingFlightData: BookingFlightData = initialBookingFlightData;
  private _bookingFlightOriginal = Object.assign({}, this._currentBookingFlightData);

  private _currentBookingDeliveryLocation: DeliveryLocation = initialDeliveryLocation;
  private _bookingDeliveryLocationOriginal = Object.assign({}, this._currentBookingDeliveryLocation);

  private _currentBookingCollectionLocation: CollectionLocation = initialCollectionLocation;
  private _bookingCollectionLocationOriginal = Object.assign({}, this._currentBookingCollectionLocation);

  private _currentBookingMakePayment: MakePaymentRequest = initialMakePaymentRequest;
  private _bookingMakePaymentOriginal = Object.assign({}, this._currentBookingMakePayment);

  private _postBookingSubscription: Subscription;
  private _makePaymentRequestSubscription: Subscription;
  private _removeBookingReferenceSubscription: Subscription;
  private _getBookingReferenceSubscription: Subscription;
  private _airportDataResponse;
  private _token = '';
  private _googlePaymentToken = '';
  private _applePaymentToken = '';
  private _oldPrice;
  private _amendBookingFlag = 0;
  private departureCountryCode;
  private bookingProduct;
  private arrivalCountryCode;

  constructor(
    private _appStore: Store<AppState>,
    private _http: HttpClient,
    private _router: Router,
    private _errorService: ErrorHandler,
    private _dataStorageService: DataStorageService,
    private _sharedService: SharedService,
    private _loginService: LoginService,
    private _abandonmentService: AbandonmentService,
    private _appLoaderService: AppLoaderService,
    private _bagItemsService: BagItemsService,
    private _eventsService: EventsService,
  ) {
    this._removeBookingReferenceSubscription = this._errorService.removeBookingReference$
      .subscribe(removeRef => {
        this.updateBookingReference(null);
        this.updateBookingJourneyReference(null);
        this._appStore.dispatch(updateBookingReference({payload: ''}));
        this._appStore.dispatch(updateBookingJourneyReference({payload: ''}));
        this._removeBookingReferenceSubscription.unsubscribe();
      });
  }

  updateDataVariable(val: any) {
    this.dataLinkVariableUpdated.next(val);
  }

  createAuthorizationHeader(headers: HttpHeaders, token) {
    headers = headers.set('Authorization', 'bearer ' + token);
    return headers;
  }

  // get store state
  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1))
      .subscribe(s => state = s);
    return state;
  }


  // set get google payment token

  setGooglePaymentToken(token) {
    this._googlePaymentToken = token;
    this._appStore.dispatch(googlePayToken({payload: token}));
  }
  getGooglePaymentToken() {
    const state = this.getState(this._appStore);
    const d2dReducer = state.d2dReducer;
    return d2dReducer.googlePayToken;
  }

  setApplePaymentToken(token) {
    this._appStore.dispatch(applePayToken({payload: token}));
 }
  getApplePaymentToken() {
    const state = this.getState(this._appStore);
    const bookingState = JSON.parse(JSON.stringify(state));
    const d2dReducer = bookingState.d2dReducer;

    return d2dReducer.applePayToken;
  }

  setOldPriceService(oldprice) { this._oldPrice = oldprice; }
  getOldPriceService() { return this._oldPrice }


  // set get api token
  setApiToken(token) { this._token = token; }
  getApiToken() { return this._token; }

  // return all airport data
  getAirportData() {

    const returnedData: { hasError: boolean, data: any } = this._sharedService.getAirports();

    // airport data api call fallback
    // check if aiport data exists - called on server
    // if ok - pass data in to subject
    // if error - call api again, pass data back in callback - then pass in subject
    try {
      if (returnedData.hasError) {
        this.getAllAirports$.next('error');
        this._errorService.handleError(returnedData.data);
        this._appStore.dispatch(AIRPORTS_LOADED({ airportLocationsLoaded: false }));
        return;
      } else {
        this._airportDataResponse = returnedData.data;
        this.getAllAirports$.next(returnedData.data);
        this._appStore.dispatch(AIRPORTS_LOADED({ airportLocationsLoaded: true }));
      }
    } catch (error) {
      this._sharedService.getAirports(true, (d) => {
        if (d.hasError) {
          this.getAllAirports$.next('error');
          this._errorService.handleError(d.data);
          this._appStore.dispatch(AIRPORTS_LOADED({ airportLocationsLoaded: false }));
          return;
        } else {
          this._airportDataResponse = d.data;
          this.getAllAirports$.next(d.data);
          this._appStore.dispatch(AIRPORTS_LOADED({ airportLocationsLoaded: true }));
        }
      });
    }

  }

  // create init booking object
  createBookingInit(bookingProduct) {

    this._appStore.dispatch(CURRENT_BOOKING_LOADED({ currentBookingLoaded: false }));
    const state: any = this.getState(this._appStore);


    this.currentBooking = Object.assign({}, this._bookingOriginal);

    const data = JSON.parse(JSON.stringify(this.currentBooking));
    // reset refund request to null on init
    data.refundRequest = null;
    this.currentBooking = data;

    this.bookingStored$.next(false);

    if (this.currentBooking.bookingJourney.length === 0) {
      data.bookingJourney.push(this._bookingJourneyOriginal);
      this.currentBooking = data;
    }
    data.bookingJourney[0].flightData = this._bookingFlightOriginal;

    data.bookingJourney[0].journeyDirection = null;
    data.bookingJourney[0].journeyDirection = state.bookingReducer.bookingDetails.journeyLegDetails[0].journeyDirection;

    data.bookingJourney[0].collectionDateTimeUTC = '';
    data.bookingJourney[0].deliveryDateTimeUTC = '';

    data.bookingJourney[0].collectionLocation = Object.assign({}, this._bookingCollectionLocationOriginal);
    data.bookingJourney[0].deliveryLocation = Object.assign({}, this._bookingDeliveryLocationOriginal);
    data.makePaymentRequest = Object.assign({}, this._bookingMakePaymentOriginal);

    data.bookingJourney[0].note.text = '';

    data.bookingCompletion.collectionLocationDetails = false;
    data.bookingCompletion.collectionDateTimeDetails = false;
    data.bookingCompletion.deliveryLocationDetail = false;
    data.bookingCompletion.deliveryDateTimeDetails = false;
    data.bookingCompletion.bookingComplete = false;

    this.currentBooking = data;

    // // console.log(this.getState(this._appStore).flightReducer.flightDetails.flightProducts.ccd, 'this.currentBooking');
    // // console.log(this.getState(this._appStore).flightReducer.flightDetails.flightResponse.flightDirection, 'this.currentBooking');

    const ccd = this.getState(this._appStore).flightReducer.flightDetails.flightProducts.ccd;
    const flightDirection = this.getState(this._appStore).flightReducer.flightDetails.flightResponse.flightDirection;

    // const ccd = true;
    // const flightDirection = 2;
    // console.log('bookingProduct', bookingProduct)
    if (bookingProduct === 'ccd') {
      this.updateBookingProductToCCD();
    } else if (bookingProduct === 'abc') {
      this.updateBookingProductToABC();
    }

    if (ccd && flightDirection === 2) {
      // rename to ccd
      this.updateBookingProductCCD();
    }

    // get booking reference
    this.getBookingReferenceForRequest();

    // get airport data from api
    this.getAirportData();
    this.updateBookingObject(this.currentBooking);

    // update add booking items
    // used to be done in old flight service
    this.updateCollectionOrDeliveryDateTime(state.flightReducer.flightDetails.flightResponse.productAvailability.journeyDirection, '');
    this.updateTimeslotReservation({ timeSlotReservationId: '', reservationEndTime: '', reservationStartTime: '', timeSlotType: null });
    if (state.flightReducer.flightDetails.flightResponse.productAvailability.journeyDirection === 1) {
      this.updateBookingFlagsCollectionDateTime(false);
    } else {
      this.updateBookingFlagsDeliveryDateTime(false);
    }

    this.updateVirtualBags(state.bookingReducer.bookingDetails.virtualBagCount);
    this.updateBagItems(state.bookingReducer.bookingDetails.bagItems);

    data.dataLinkSessionId = this.updateSessionId();
    data.returnSessionId = this.updateReturnJourneyId();

    const selectedMultiLegFlight = JSON.parse(JSON.stringify(this.getState(this._appStore).flightFinderReducer.selectedMultiLegFlight));

    const uuid = uuid4();
    this._appStore.dispatch(saveUuid({payload: uuid}));
    // if ((it is actually multileg flight || user clicks on 1+stop) and we have flight details) {
    //  code logic
    //  changes done for 9488 & 9651
    // }
    if ((selectedMultiLegFlight?.flightStatusDetails?.length > 1 || state.flightFinderReducer.requestLegs > 1) && selectedMultiLegFlight && state.bookingReducer.bookingDetails.bookingType === 'new') {
      selectedMultiLegFlight.flightInfo.id = uuid;
    }

    data.bookingJourney[0].flightDetails = selectedMultiLegFlight;
    this.currentBooking = data;
    // if has return session
    // pass values into current booking object
    if (this.getState(this._appStore).flightFinderReducer.returnSessionString !== null) {

      const parsedData = JSON.parse(this.getState(this._appStore).flightFinderReducer.returnSessionString);

      this.updateVirtualBags(parsedData.numberOfBags);
      this.updateBagItems(parsedData.bagItems);
      this._appStore.dispatch(updateVirtualBags({ payload: parsedData.numberOfBags }));
      this._appStore.dispatch(updateBagItems({ payload: parsedData.bagItems }));

      if (parsedData.journeyDirection === 1) {
        this.updateBookingFlagsCollectionLocation(true);
        this.updateCollectionOrDeliveryLocation(parsedData.journeyDirection, parsedData.collectionLocation);
      } else if (parsedData.journeyDirection === 2) {
        this.updateBookingFlagsDeliveryLocation(true);
        this.updateCollectionOrDeliveryLocation(parsedData.journeyDirection, parsedData.deliveryLocation);
      }

      if (parsedData.customer !== null) {
        // if current logged in user doesn't match return session user, logout
        const currUser = this.getState(this._appStore).userReducer;

        if (currUser.userDetails.userLoggedIn && (currUser.userDetails.currentUser.emailAddress !== parsedData.customer.emailAddress)) {
          this._loginService.logout();

          this.updateBooker({
            firstName: parsedData.customer.firstName,
            lastName: parsedData.customer.lastName,
            phoneNumber: parsedData.customer.phoneNumber,
            countryCode: parsedData.customer.countryCode,
            emailAddress: parsedData.customer.emailAddress
          });

          this._appStore.dispatch(UPDATE_USER_EMAIL({ userEmail: parsedData.customer.emailAddress }));
        } else if (!currUser.userDetails.userLoggedIn) {
          try {
            this.updateBooker({
              firstName: parsedData.customer.firstName,
              lastName: parsedData.customer.lastName,
              phoneNumber: parsedData.customer.phoneNumber,
              countryCode: parsedData.customer.countryCode,
              emailAddress: parsedData.customer.emailAddress
            });
          } catch (error) {
            // console.log('error adding in user details form return session');
          }
        }
      }

    }

    if (state.bookingReducer.bookingDetails.abTestingDataQuote.bookingPodEnv !== null && state.bookingReducer.bookingDetails.abTestingDataQuote.bookingPodVersion !== null) {
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].bookingPodEnv = state.bookingReducer.bookingDetails.abTestingDataQuote.bookingPodEnv;
      data.bookingJourney[0].bookingPodVersion = state.bookingReducer.bookingDetails.abTestingDataQuote.bookingPodVersion;
      this.currentBooking = data;
    }

    if (state.bookingReducer.bookingDetails.abTestingDataForm.bookingFormEnv !== null && state.bookingReducer.bookingDetails.abTestingDataForm.bookingFormVersion !== null) {
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].bookingFormEnv = state.bookingReducer.bookingDetails.abTestingDataForm.bookingFormEnv;
      data.bookingJourney[0].bookingFormVersion = state.bookingReducer.bookingDetails.abTestingDataForm.bookingFormVersion;
      this.currentBooking = data;
    }

    // check for referral (UTM) cookies
    const cookieUtmSource = this._dataStorageService.getCookie('airportr_original_partner_source');
    const cookieUtmCampaign = this._dataStorageService.getCookie('airportr_original_partner_campaign');
    const cookieUtmContent = this._dataStorageService.getCookie('airportr_original_partner_content');

    if (cookieUtmSource && cookieUtmCampaign && cookieUtmContent) {
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].utmSource = cookieUtmSource;
      data.bookingJourney[0].utmCampaign = cookieUtmCampaign;
      data.bookingJourney[0].utmContent = cookieUtmContent;
      this.currentBooking = data;
    }

    this.bookingInitFinished = true;
    this._appStore.dispatch(CURRENT_BOOKING_LOADED({ currentBookingLoaded: this.bookingInitFinished }));
  }
  // create init from stored data
  createBookingFromStore(booking: any) {
    const state: any = this.getState(this._appStore);
    const data = JSON.parse(JSON.stringify(booking.currentBooking));

    const flightData = state.flightReducer.flightDetails.flightResponse;
    const flightLeg = flightData.flightStatusDetails[0].flightLeg;
    const flightStatusDetails = flightData.flightStatusDetails[flightLeg -1];
    const flightBookingData = {
      departureAirportCode: flightStatusDetails.departureAirport.airportCode,
      flightInfoId: flightData.flightInfo.id,
      requestFlightNumber: flightData.flightInfo.requestFlightNumber,
      airportCityName: flightStatusDetails.departureAirport.airportCityName,
      flightLeg: flightStatusDetails.flightLeg,
      requestDepartureDate: flightStatusDetails.departureDateTimeDetails.scheduledDateTimeLocal
    };
    data.bookingJourney[0].flightData =  flightBookingData;




    const selectedMultiLegFlight = JSON.parse(JSON.stringify(this.getState(this._appStore).flightFinderReducer.selectedMultiLegFlight));

    const uuid = uuid4();
    this._appStore.dispatch(saveUuid({payload: uuid}));
    if ((selectedMultiLegFlight?.flightStatusDetails?.length > 1 || state.flightFinderReducer.requestLegs > 1) && selectedMultiLegFlight && state.bookingReducer.bookingDetails.bookingType === 'amend') {
      selectedMultiLegFlight.flightInfo.id = uuid;
    }

    data.bookingJourney[0].flightDetails = selectedMultiLegFlight;
    this.currentBooking = data;

    data.bookingJourney[0].flightDetails = selectedMultiLegFlight;
    this.currentBooking = data;

    this._appStore.dispatch(CURRENT_BOOKING_LOADED({ currentBookingLoaded: false }));

    this.getAirportData();

    this.currentBooking = data;
    // if the currentBooking obj is missing the refundRequest Obj (old bookings)
    // add it in as null
    if (typeof this.currentBooking.refundRequest === 'undefined') {
      //this.currentBooking.refundRequest = null;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.refundRequest = null;
      this.currentBooking = data;
    }

    this.updateBookingObject(this.currentBooking);

    this.bookingInitFinished = true;
    this._appStore.dispatch(CURRENT_BOOKING_LOADED({ currentBookingLoaded: this.bookingInitFinished }));
  }

  updateBookingProductToCCD() {
    //this.currentBooking.bookingJourney[0].addOnProductType = [];
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].addOnProductType = [];
    data.bookingJourney[0].addOnProductType.push(2);
    this.currentBooking = data;
  }
  updateBookingProductToABC() {
    //this.currentBooking.bookingJourney[0].addOnProductType = [];
    //this.currentBooking.bookingJourney[0].addOnProductType.push(1);
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].addOnProductType = [];
    data.bookingJourney[0].addOnProductType.push(1);
    this.currentBooking = data;
  }
  updateBookingProductCCD() {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].addOnProductType = [];
    data.bookingJourney[0].addOnProductType.push(2);
    this.currentBooking = data;
  }
  updateBookingDirection(direction) {
    this.currentBooking.bookingJourney[0].journeyDirection = direction;
  }
  updateSessionId() {
    const state = this.getState(this._appStore);
    return state.flightFinderReducer.dataLinkSession;
  }

  updateReturnJourneyId() {
    const state = this.getState(this._appStore);
    return state.flightFinderReducer.returnSessionId;
  }

  updateBookingFlight(objFlight: any) {
    //this.currentBooking.bookingJourney[0].flightData = objFlight;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].flightData = objFlight;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateManualFlightData(manualFlight: any) {
    //this.currentBooking.bookingJourney[0].flightData = initialBookingFlightData;
    //this.currentBooking.bookingJourney[0].manualFlightData = manualFlight;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].flightData = initialBookingFlightData;
    data.bookingJourney[0].manualFlightData = manualFlight;
    this.currentBooking = data;
    this._appStore.dispatch(updateManualDestination({ payload: manualFlight }));
    this.updateBookingObject(this.currentBooking);
    this.manualFlightDataStored$.next(true);
  }

  updateAirportTime(direction: Number, value: any) {
    if (direction === 1) {
      //this.currentBooking.bookingJourney[0].deliveryDateTimeUTC = value;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].deliveryDateTimeUTC = value;
      this.currentBooking = data;
    } else if (direction === 2) {
      //this.currentBooking.bookingJourney[0].collectionDateTimeUTC = value;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionDateTimeUTC = value;
      this.currentBooking = data;
    }
  }

  updateAirportLocation(journeyDirection: Number, code: string, terminal?: string) {

    let direction = '';

    if (terminal === null || typeof terminal === 'undefined') { terminal = ''; }
    if (code === 'LHR') { direction = '(Dep)'; }

    function filterByCode(obj) {
      if (obj.shortName.indexOf(code) !== -1 && obj.shortName.indexOf(terminal) !== -1 && obj.shortName.indexOf(direction) !== -1) {
        return obj;
      }
    }

    const filteredList = this._airportDataResponse.locations.filter(filterByCode);
    const airportDetails = filteredList[0];

    if (journeyDirection === 1) {
      //this.currentBooking.bookingJourney[0].deliveryLocation = airportDetails;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].deliveryLocation = airportDetails;
      this.currentBooking = data;
    } else if (journeyDirection === 2) {
      //this.currentBooking.bookingJourney[0].collectionLocation = airportDetails;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionLocation = airportDetails;
      this.currentBooking = data;
    }
    //
    this._appStore.dispatch(updateBookingObject({ payload: this.currentBooking }));
    if(journeyDirection === 1) {
      this._appStore.dispatch(updateOutboundBookingObject({ payload: this.currentBooking}));
    } else {
      this._appStore.dispatch(updateInboundBookingObject({ payload: this.currentBooking}));
    }

  }

  updateCollectionOrDeliveryLocation(direction: Number, obj: any) {
    if (direction === 1) {
      //this.currentBooking.bookingJourney[0].collectionLocation = obj;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionLocation = obj;
      this.currentBooking = data;
    } else if (direction === 2) {
      //this.currentBooking.bookingJourney[0].deliveryLocation = obj;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].deliveryLocation = obj;
      this.currentBooking = data;
    }
    this.updateBookingObject(this.currentBooking);
  }

  resetCollectionOrDeliveryLocation(direction: Number) {
    if (direction === 1) {
      // this.currentBooking.bookingJourney[0].collectionLocation = this._bookingCollectionLocationOriginal;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionLocation = this._bookingCollectionLocationOriginal;
      this.currentBooking = data;
      this.updateBookingFlagsCollectionLocation(false);
    } else if (direction === 2) {
      // this.currentBooking.bookingJourney[0].deliveryLocation = this._bookingDeliveryLocationOriginal;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].deliveryLocation = this._bookingCollectionLocationOriginal;
      this.currentBooking = data;
      this.updateBookingFlagsDeliveryLocation(false);
    }
    this.updateBookingObject(this.currentBooking);
  }

  updateBookingFlagsCollectionLocation(val) {
    //this.currentBooking.bookingCompletion.collectionLocationDetails = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.collectionLocationDetails = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }
  updateBookingFlagsCollectionDateTime(val) {
    //this.currentBooking.bookingCompletion.collectionDateTimeDetails = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.collectionDateTimeDetails = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }
  updateBookingFlagsDeliveryLocation(val) {
    //this.currentBooking.bookingCompletion.deliveryLocationDetail = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.deliveryLocationDetail = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }
  updateBookingFlagsDeliveryDateTime(val) {
    //this.currentBooking.bookingCompletion.deliveryDateTimeDetails = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.deliveryDateTimeDetails = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }
  updateBookingFlagsBooking(val) {
    //this.currentBooking.bookingCompletion.bookingComplete = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.bookingComplete = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateTimeslotProductTierId(val: string) {
    // this.currentBooking.bookingJourney[0].productTierId = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].productTierId = val;
    this.currentBooking = data;
  }

  updateTimeslotId(val: string) {
    //this.currentBooking.bookingJourney[0].timeSlotId = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].timeSlotId = val;
    this.currentBooking = data;
  }

  updateTimeslotReservation(obj: TimeslotBookingObject) {
    //this.currentBooking.timeSlotReservation = obj;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.timeSlotReservation = obj;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateCollectionOrDeliveryDateTime(direction: Number, date: string) {
    if (direction === 1) {
      //this.currentBooking.bookingJourney[0].collectionDateTimeUTC = date;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionDateTimeUTC = date;
      this.currentBooking = data;
    } else if (direction === 2) {
      //this.currentBooking.bookingJourney[0].deliveryDateTimeUTC = date;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionDateTimeUTC = date;
      this.currentBooking = data;
    }
    this.updateBookingObject(this.currentBooking);
  }

  resetTimeslotReservation() {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.timeSlotReservation = {
      timeSlotReservationId: '',
      reservationStartTime: '',
      reservationEndTime: '',
      timeSlotType: null
    };
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  // swap delivery/collection data
  // for direction = 2
  updateDataAirportToLocation() {
    const newCollectionLocation = this.currentBooking.bookingJourney[0].deliveryLocation;
    const newCollectionDateTime = this.currentBooking.bookingJourney[0].deliveryDateTimeUTC;
    const newDeliveryLocation = this.currentBooking.bookingJourney[0].collectionLocation;
    const newDeliveryDateTime = this.currentBooking.bookingJourney[0].collectionDateTimeUTC;

    const data = JSON.parse(JSON.stringify(this.currentBooking));

    data.bookingJourney[0].collectionLocation = newCollectionLocation;
    data.bookingJourney[0].collectionDateTimeUTC = newCollectionDateTime;
    data.bookingJourney[0].deliveryLocation = newDeliveryLocation;
    data.bookingJourney[0].deliveryDateTimeUTC = newDeliveryDateTime;
    this.currentBooking = data;

    this.updateBookingObject(this.currentBooking);

  }

  updateBooker(item: BookerItem) {
    //this.currentBooking.bookingUser = data;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    this._appStore.dispatch(bookerDataBackup({payload: {...data.bookingUser}}));
    data.bookingUser = item;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }


  validatePhoneNumber(number) {

    const request = {
      phoneNumber: number
    };

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);


    this._http
      .post(API_PATH + VALIDATE_PHONE_NUMBER, request, { headers: headers })
      .subscribe(
        (res: any) => {
          this.validPhoneNumberSubject$.next(res.isValidPhoneNumber);
        },
        err => {
          this._errorService.handleError(err);
          return;
        });
  }

  updateNewsletterOptIn(val: Boolean) {
    //this.currentBooking.hasMarketingEnrolment = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.hasMarketingEnrolment = val;
    this.currentBooking = data;
  }

  updateCustomsForInspection(val: boolean){
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.HasAirFranceGVACustomsAgreement = val;
    this.currentBooking = data;
  }

  updateDataSharingAllowed(val: Boolean) {
    //this.currentBooking.dataSharingAllowed = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.dataSharingAllowed = val;
    this.currentBooking = data;
  }

  updateSmsMarketingReminder(val: Boolean) {
    //this.currentBooking.smsMarketingReminder = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.smsMarketingReminder = val;
    this.currentBooking = data;
  }

  updateEmiratesSkywardsAcknowledgment(val: Boolean) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.hasEmiratesSkywardsAcknowledgment = val;
    this.currentBooking = data;
  }

  updateVirtualBags(value) {
    //this.currentBooking.bookingJourney[0].virtualBagCount = parseInt(value, 10);
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].virtualBagCount = parseInt(value, 10);
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
    // update d2d state
    setTimeout(() => {
      this.updateD2dstate();
    },);
  }

  updateBagItems(items) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].virtualBagClassification = this._bagItemsService.bagItemsToBagsClassification(items);
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateBagNote(value) {
    //this.currentBooking.bookingJourney[0].note.text = value;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].note.text = value;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);

  }

  updateHasOversizedBag(value) {
    //this.currentBooking.bookingJourney[0].hasOversizedBag = value;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].hasOversizedBag = value;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);

  }

  getBookingReferenceForRequest() {

     // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

      const state = this.getState(this._appStore);
      const flightDirection = state.flightReducer.flightDetails.flightResponse.flightDirection;
      let flightDetails = flightDirection ===  1 ? state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0] : state.flightReducer.flightDetails.flightResponse.flightStatusDetails[state.flightReducer.flightDetails.flightResponse.flightStatusDetails.length - 1];

      if (state.flightFinderReducer.selectedMultiLegFlight) {
        flightDetails = flightDirection ===  1 ? state.flightFinderReducer.selectedMultiLegFlight.flightStatusDetails[0] : state.flightFinderReducer.selectedMultiLegFlight.flightStatusDetails[state.flightFinderReducer.selectedMultiLegFlight.flightStatusDetails.length - 1];
      }

      const airlineCode = flightDetails.airlineCode
      const airportCode = flightDirection ===  1 ? flightDetails.departureAirport.airportCode : flightDetails.arrivalAirport.airportCode

      const request: GenerateBookingReference = {
        addOnProductType: [flightDirection ===  1 ? 'abc' : 'CarouselCollection'],
        airlineCode: airlineCode,
        airportCode: airportCode,
        preAcceptanceType: 'None',
    }

    if (state.hubDropReducer.selectedPostOffice && state.hubDropReducer.selectedCollectionType === 'atPostOffice') {
      request.preAcceptanceType = 'HubDrop';
    }

    // if we already have booking references use that instead
    const bookingReferenceState = state.bookingReducer.bookingDetails.bookingReferenceState;
    const { bookingReference: hubdropRef } = bookingReferenceState?.hubdropBookingReference || {};
    const { bookingReference: simpleRef } = bookingReferenceState?.simpleBookingReference || {};

    const isHubDrop = request.preAcceptanceType === 'HubDrop' && hubdropRef;
    const isNone = request.preAcceptanceType === 'None' && simpleRef;
    const isABCReference = isNone && simpleRef?.startsWith('AP1') && request.addOnProductType[0] === 'abc';
    const isCCDReference = isNone && simpleRef?.startsWith('AP2') && request.addOnProductType[0] === 'CarouselCollection';

    if (isHubDrop) {
      this.generateBookingReferenceResponseHandler(bookingReferenceState.hubdropBookingReference, request);
      return;
    } else if (isABCReference || isCCDReference) {
      this.generateBookingReferenceResponseHandler(bookingReferenceState.simpleBookingReference, request);
      return;
    }

    const promise = new Promise((resolve, reject) => {
      this._http
      .post(API_PATH + GENERATE_BOOKING_REFERENCE, request, { headers: headers })
      .toPromise().then((res: any) => {
        if (request.preAcceptanceType === 'HubDrop') {
          const bookingRefState = Object.assign({}, state.bookingReducer.bookingDetails.bookingReferenceState, { hubdropBookingReference: res });
          this._appStore.dispatch(updateBookingReferenceState({ payload: bookingRefState }));
        } else {
          const bookingRefState = Object.assign({}, state.bookingReducer.bookingDetails.bookingReferenceState, { simpleBookingReference: res });
          this._appStore.dispatch(updateBookingReferenceState({ payload: bookingRefState }));
        }

        this.generateBookingReferenceResponseHandler(res, request);
        resolve(res);
      }
      ).catch(err => {
        this._errorService.handleError(err);
        reject(err);
        return;
      });
    });

    return promise;
  }

  private generateBookingReferenceResponseHandler(res: any, request: GenerateBookingReference) {
    this.generateBooking$.next(true);
    this.updateBookingReference(res.bookingReference);
    this.updateBookingJourneyReference(res.bookingJourneyReference);
    this.updateUniqueSequenceIdentifier(res.uniqueSequenceIdentifier);
    this._appStore.dispatch(updateBookingReference({payload: res.bookingReference}));
    this._appStore.dispatch(updateBookingJourneyReference({payload: res.bookingJourneyReference}));
    this._appStore.dispatch(updateUniqueSequenceId({ payload: res.uniqueSequenceIdentifier }));

    this._appStore.dispatch(UPDATE_USER_AGENT_BOOKING_REFERENCE({ bookingRef: res.bookingReference }));

    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.generateBookingReferenceRequest = request;
    this.currentBooking = data;
    this._dataStorageService.saveData();
  }

  updateBookingReference(val: string) {
    //this.currentBooking.bookingReference = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingReference = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateBookingJourneyReference(val: string) {
    //this.currentBooking.bookingReference = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourneyReference = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateUniqueSequenceIdentifier(val: string) {
    //this.currentBooking.bookingReference = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.uniqueSequenceIdentifier = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateBookingInstance(val: any) {
    const data = JSON.parse(JSON.stringify(val));
    this.currentBooking = data.currentBooking;
  }

  updatePayment(request: PaymentRequest, promoRequest: any, isCustomerService: boolean, billingAddress: BillingAddress, product: string, loggedIn: Boolean, retryRequest?: Boolean, type?: string) {

    const makePaymentRequest: MakePaymentRequest = Object.assign({}, this._bookingMakePaymentOriginal);
    const isSbbSubsidyApplied =  this.getState(this._appStore).bookingReducer.bookingDetails.isSbbSubsidyApplied;

    makePaymentRequest.bookingReference = request.bookingReference;
    makePaymentRequest.bookingJourneyReference = '';
    makePaymentRequest.transactionType = request.transactionType;
    makePaymentRequest.businessTransactionType = request.businessTransactionType;
    makePaymentRequest.totalAmount.amount = request.fullAmount;
    makePaymentRequest.totalAmount.currency = request.currency;
    makePaymentRequest.isUserLoggedIn = !!this.getState(this._appStore).userReducer.userDetails?.userToken;

    // if bag type or bag bag count change
    const quoteBagsTypeCount = this.getState(this._appStore).flightFinderReducer.quoteFlightRequest?.bagItems;
    const quoteBagCount = this.getState(this._appStore).flightFinderReducer.quoteFlightRequest?.bagCount;
    const selectedBagTypeCount = this.getState(this._appStore).bookingReducer.bookingDetails.bagItems;
    if (quoteBagsTypeCount && (quoteBagsTypeCount.bags !== selectedBagTypeCount.bags || quoteBagsTypeCount.childEquipment !== selectedBagTypeCount.childEquipment || quoteBagsTypeCount.oversizeItem !== selectedBagTypeCount.oversizeItem)) {
      makePaymentRequest.isBagTypeChanged = true;
    }

    if (quoteBagCount && quoteBagCount !== this.getState(this._appStore).bookingReducer.bookingDetails.virtualBagCount) {
      makePaymentRequest.isBagCountChanged = true;
    }

    // makePaymentRequest.googlePayment.googlePayToken = request.googlePayToken;

    if(request.applePayToken) {
      makePaymentRequest.applePayment = {
        applePayToken: request.applePayToken,
        amount: request.amount, // current booking price
        upsellBookingAmount: 0, // d2d booking price
      };
      makePaymentRequest.cardDetail = null;
    } else if (request.googlePayToken) {
      makePaymentRequest.googlePayment = {
        googlePayToken: request.googlePayToken,
        amount: request.amount, // current booking price
        upsellBookingAmount: 0, // d2d booking price
      };
      makePaymentRequest.cardDetail = null;
    } else {
      makePaymentRequest.cardDetail.obscuredCardNumber = request.obscuredCardNumber;
      makePaymentRequest.cardDetail.encryptedCardInformation = request.encodedCardInformation;
      makePaymentRequest.cardDetail.amount = request.amount;
      makePaymentRequest.cardDetail.saveCard = request.saveCard;
    }

    if (makePaymentRequest.cardDetail?.amount === 0) {
      makePaymentRequest.cardDetail.obscuredCardNumber = '';
    }

    if(isSbbSubsidyApplied){
      makePaymentRequest.sbbSubsidyAmount = this.getState(this._appStore).priceReducer.sbbSubsidyAmount;
    }

    const oversizeItemSurchange = this.getState(this._appStore).priceReducer.price.pricingResponse[0].pricingBreakdown.oversizeSurchargePriceMajor;
    makePaymentRequest.oversizeSurchargePrice = oversizeItemSurchange ? oversizeItemSurchange  * 100 : 0;
    this.updateHasOversizedBag(!!oversizeItemSurchange);

    if (request.recurringReference !== null && request.recurringReference !== 'newCard') {
      makePaymentRequest.recurringPayment.recurringDetailReference = request.recurringReference;
    } else {
      makePaymentRequest.recurringPayment = null;
    }

    makePaymentRequest.quoteId = request.quoteId;
    makePaymentRequest.transactionType = request.transactionType;
    makePaymentRequest.isPaidByInvoice = request.isPaidByInvoice;

    if (promoRequest) {
      makePaymentRequest.promotionRedemption = promoRequest;

    }

    makePaymentRequest.productTierId = this.currentBooking.bookingJourney[0].productTierId;
    makePaymentRequest.timeslotId = this.currentBooking.bookingJourney[0].timeSlotId;
    makePaymentRequest.flightDateTime = this.currentBooking.bookingJourney[0].flightData.requestDepartureDate;

    makePaymentRequest.isCustomerService = isCustomerService;

    makePaymentRequest.shopperInfo.billingAddress = billingAddress;

    const browserInf = ThreedDS2Utils.getBrowserInfo();
    makePaymentRequest.shopperInfo.browserInfo.acceptHeader = '';
    makePaymentRequest.shopperInfo.browserInfo.userAgent = browserInf.userAgent;
    makePaymentRequest.shopperInfo.browserInfo.language = browserInf.language;
    makePaymentRequest.shopperInfo.browserInfo.colorDepth = browserInf.colorDepth;
    makePaymentRequest.shopperInfo.browserInfo.screenHeight = browserInf.screenHeight;
    makePaymentRequest.shopperInfo.browserInfo.screenWidth = browserInf.screenWidth;
    makePaymentRequest.shopperInfo.browserInfo.timeZoneOffset = browserInf.timeZoneOffset;
    makePaymentRequest.shopperInfo.browserInfo.javaEnabled = browserInf.javaEnabled;
    makePaymentRequest.shopperInfo.IP = this.getState(this._appStore).userAgentReducer.ip;
    makePaymentRequest.shopperInfo.email = this.currentBooking.bookingUser.emailAddress;

    const milesReducer = this.getState(this._appStore).milesAndMoreReducer;
    if (milesReducer.isUserLoggedIn) {
      makePaymentRequest.milesInfo = {
        serviceCardNumber: milesReducer.miles.serviceCardNumber,
      }
      if (milesReducer.milesRedeemed) {
        const milesDiscount = this.getState(this._appStore).priceReducer.price.pricingResponse[0].pricingBreakdown.milesDiscountMinor;
        makePaymentRequest.milesInfo.transactionId = milesReducer.payByMilesResponse.transactionId;
        makePaymentRequest.milesInfo.currencyRatio = milesReducer.miles.currencyRatio;
        makePaymentRequest.milesInfo.milesDiscount = milesDiscount;
      }
    }

    // this.currentBooking.makePaymentRequest = makePaymentRequest;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.makePaymentRequest = makePaymentRequest;
    this.currentBooking = data;

    if (promoRequest) {
      const updatedGenerateBookingReferenceRequest = { ...data.generateBookingReferenceRequest };
      updatedGenerateBookingReferenceRequest.promotionType = this.getState(this._appStore).priceReducer.price.promotionType;
      data.generateBookingReferenceRequest = updatedGenerateBookingReferenceRequest;
      this.currentBooking = data;
    }

    if (retryRequest) {
      this.postPaymentRequest(makePaymentRequest, loggedIn, product, type);
    } else {
      this.postBooking(loggedIn, product, request.applePayToken, request.googlePayToken);
    }

  }

  updatePaymentForAmends(request: PaymentRequest, promoRequest: any, isCustomerService: boolean, billingAddress: BillingAddress, amendData: any) {
    const state = this.getState(this._appStore);
    const makePaymentRequest: MakePaymentRequest = Object.assign({}, this._bookingMakePaymentOriginal);
    const isSbbSubsidyApplied =  state.bookingReducer.bookingDetails.isSbbSubsidyApplied;;

    makePaymentRequest.bookingReference = request.bookingReference;
    makePaymentRequest.bookingJourneyReference = '';
    makePaymentRequest.totalAmount.amount = request.fullAmount;
    makePaymentRequest.totalAmount.currency = request.currency;
    makePaymentRequest.totalAmount.baseCurrency = ISO_CURRENCY_CODES[state.priceReducer.price.totalPrice.baseCurrency];
    makePaymentRequest.totalAmount.exchangeRate = state.priceReducer.price.totalPrice.exchangeRate;
    makePaymentRequest.businessTransactionType = request.businessTransactionType;
    makePaymentRequest.isUserLoggedIn = !!this.getState(this._appStore).userReducer.userDetails?.userToken;
    const milesDiscount = state.priceReducer.price.pricingResponse[0].pricingBreakdown.milesDiscountMinor;
    if (milesDiscount > 0) {
      makePaymentRequest.totalAmount.amount = request.fullAmount + milesDiscount;
    }

    if(request.applePayToken) {
      makePaymentRequest.applePayment = {
        applePayToken: request.applePayToken,
        amount: request.amount, // current extra booking charge / refund
        upsellBookingAmount: 0, // d2d booking price
      };
      makePaymentRequest.cardDetail = null;
    } else if (request.googlePayToken) {
      makePaymentRequest.googlePayment = {
        googlePayToken: request.googlePayToken,
        amount: request.amount, // current extra booking charge / refund
        upsellBookingAmount: 0, // d2d booking price
      };
      makePaymentRequest.cardDetail = null;
    } else {
      makePaymentRequest.cardDetail.obscuredCardNumber = request.obscuredCardNumber;
      makePaymentRequest.cardDetail.encryptedCardInformation = request.encodedCardInformation;
      makePaymentRequest.cardDetail.amount = request.amount;
      makePaymentRequest.cardDetail.saveCard = request.saveCard;
    }

    const attachedCard = this.getState(this._appStore).currentBookingReducer.currentBooking.makePaymentRequest?.cardDetail?.obscuredCardNumber
    if (makePaymentRequest.cardDetail?.amount === 0 && !attachedCard) {
      makePaymentRequest.cardDetail.obscuredCardNumber = '';
    }

    if(isSbbSubsidyApplied){
      makePaymentRequest.sbbSubsidyAmount = state.priceReducer.sbbSubsidyAmount;
    }

    const oversizeItemSurchange= state.priceReducer.price.pricingResponse[0].pricingBreakdown.oversizeSurchargePriceMajor;

    // check if oversize item surcharge for added in original booking
    const oversizeItems = this.getState(this._appStore).utilityReducer.bagItems.oversizeItem;

    if(!oversizeItems){
      makePaymentRequest.oversizeSurchargePrice = oversizeItemSurchange ? oversizeItemSurchange  * 100 : 0;
    } else {
      makePaymentRequest.oversizeSurchargePrice = 0;
    }

    this.updateHasOversizedBag(!!oversizeItemSurchange);

    if (request.recurringReference !== null && request.recurringReference !== 'newCard') {
      makePaymentRequest.recurringPayment.recurringDetailReference = request.recurringReference;
    } else {
      makePaymentRequest.recurringPayment = null;
    }

    // makePaymentRequest.googlePayment.googlePayToken = request.googlePayToken;
    makePaymentRequest.quoteId = request.quoteId;
    makePaymentRequest.transactionType = request.transactionType;
    makePaymentRequest.isPaidByInvoice = request.isPaidByInvoice;

    if (promoRequest) {
      makePaymentRequest.promotionRedemption = promoRequest;
    }

    makePaymentRequest.productTierId = state.currentBookingReducer.amendBooking.addBookingRequest.bookingJourney[0].productTierId;
    makePaymentRequest.timeslotId = state.currentBookingReducer.amendBooking.addBookingRequest.bookingJourney[0].timeSlotId;
    makePaymentRequest.flightDateTime = this.currentBooking.bookingJourney[0].flightData.requestDepartureDate;

    makePaymentRequest.isCustomerService = isCustomerService;

    makePaymentRequest.shopperInfo.billingAddress = billingAddress;

    const milesReducer = this.getState(this._appStore).milesAndMoreReducer;

    if (milesReducer.isUserLoggedIn) {
      makePaymentRequest.milesInfo = {
        serviceCardNumber: milesReducer.miles.serviceCardNumber,
      }
      if (milesReducer.milesRedeemed) {
        const milesDiscount = this.getState(this._appStore).priceReducer.price.pricingResponse[0].pricingBreakdown.milesDiscountMinor;
        makePaymentRequest.milesInfo.transactionId = milesReducer.payByMilesResponse.transactionId;
        makePaymentRequest.milesInfo.currencyRatio = milesReducer.miles.currencyRatio;
        makePaymentRequest.milesInfo.milesDiscount = milesDiscount;
      }
    }

    const browserInf = ThreedDS2Utils.getBrowserInfo();
    makePaymentRequest.shopperInfo.browserInfo.acceptHeader = '';
    makePaymentRequest.shopperInfo.browserInfo.userAgent = browserInf.userAgent;
    makePaymentRequest.shopperInfo.browserInfo.language = browserInf.language;
    makePaymentRequest.shopperInfo.browserInfo.colorDepth = browserInf.colorDepth;
    makePaymentRequest.shopperInfo.browserInfo.screenHeight = browserInf.screenHeight;
    makePaymentRequest.shopperInfo.browserInfo.screenWidth = browserInf.screenWidth;
    makePaymentRequest.shopperInfo.browserInfo.timeZoneOffset = browserInf.timeZoneOffset;
    makePaymentRequest.shopperInfo.browserInfo.javaEnabled = browserInf.javaEnabled;
    makePaymentRequest.shopperInfo.IP = state.userAgentReducer.ip;

    //this.currentBooking.makePaymentRequest = makePaymentRequest;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.makePaymentRequest = makePaymentRequest;
    this.currentBooking = data;
    this.postAmend(amendData, null);

  }

  updateMakePaymentProductTierId(val) {
    //this.currentBooking.makePaymentRequest.productTierId = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.makePaymentRequest.productTierId = val;
    this.currentBooking = data;
  }

  updateMakePaymentTimeslotId(val) {
    //this.currentBooking.makePaymentRequest.timeslotId = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.makePaymentRequest.timeslotId = val;
    this.currentBooking = data;
  }

  updateHubdropBookingStatus(val: boolean) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.isHubdropBooking = val;
    this.currentBooking = data;
  }

  updateBookingObject(obj: any) {
    // console.log('just about to update current booking reducer')
    this._appStore.dispatch(updateBookingObject({ payload: obj }));
    if (obj.bookingJourney[0].journeyDirection === 1) {
      this._appStore.dispatch(updateOutboundBookingObject({ payload: obj}));
    } else {
      this._appStore.dispatch(updateInboundBookingObject({ payload: obj}));
    }
  }

  updateBookingZoneId(id: string) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingZoneId = id;
    this.currentBooking = data;
    this._appStore.dispatch(updateBookingObject({ payload: data }));
  }

  updateD2dstate() {
    const state = this.getState(this._appStore);
    const bookingState = JSON.parse(JSON.stringify(state));
    const flightReducer = bookingState.flightReducer;
    const bookingReducer = bookingState.bookingReducer;
    const currentBookingReducer = bookingState.currentBookingReducer;
    const priceReducer = bookingState.priceReducer;
    const timeslotReducer = bookingState.timeslotReducer;
    const flightFinderReducer = bookingState.flightFinderReducer;
    const currentBookingType = bookingState.bookingReducer.bookingDetails.bookingProduct;
    const hubDropReducer = bookingState.hubDropReducer;
    const bookingType = currentBookingType === 'abc' ? 'outboundBooking' : 'inboundBooking'
    this._appStore.dispatch(saveBookingDetails({ payload: { bookingType: bookingType, flightReducer, bookingReducer, currentBookingReducer, priceReducer, timeslotReducer, flightFinderReducer, hubDropReducer, isCompleted: state.d2dReducer[bookingType].isCompleted } }));
  }

  updateOldBookingstate() {
    const state = this.getState(this._appStore);
    const bookingState = JSON.parse(JSON.stringify(state));
    const flightReducer = bookingState.flightReducer;
    const bookingReducer = bookingState.bookingReducer;
    const currentBookingReducer = bookingState.currentBookingReducer;
    const priceReducer = bookingState.priceReducer;
    const timeslotReducer = bookingState.timeslotReducer;
    const flightFinderReducer = bookingState.flightFinderReducer;
    const currentBookingType = bookingState.bookingReducer.bookingDetails.bookingProduct;
    const hubDropReducer = bookingState.hubDropReducer;
    const bookingType = currentBookingType === 'abc' ? 'outboundBooking' : 'inboundBooking';
// this was already commented out
    // this._appStore.dispatch({ type: 'SAVE_OLD_BOOKINGS_DATA', payload: { abcBooking: abcBooking, ccdBooking: ccdBooking } });
  }

  getD2dDiscountPrice(price, milesPreview?): { outboundPrice: number; inboundPrice: number; totalD2dPrice: number} {
    let totalD2dPrice: any = 0;
    const state = this.getState(this._appStore);
    const bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
    const isEmbeddedPromo = !!state.flightFinderReducer.promoFromUrl;
    const milesRedeemed = state.milesAndMoreReducer.milesRedeemed;
    const d2d = state.d2dReducer;
    const milesReducer = state.milesAndMoreReducer;

    let inboundPrice = d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount || 0;
    let outboundPrice = d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount || 0;

    if (!state.bookingReducer.bookingDetails.isD2dService) {
      totalD2dPrice = price?.totalPrice?.majorAmount || 0;
      return { outboundPrice, inboundPrice, totalD2dPrice };
    }

    outboundPrice = +(outboundPrice.toFixed(2));
    inboundPrice = +(inboundPrice.toFixed(2));

    if (bookingProduct === 'abc') {
      outboundPrice = price?.totalPrice?.majorAmount || 0;
      totalD2dPrice = (price?.totalPrice?.majorAmount || 0) + inboundPrice;
    } else {
      inboundPrice = price?.totalPrice?.majorAmount;
      totalD2dPrice = (price?.totalPrice?.majorAmount || 0) + outboundPrice;
    }

    if (Object.keys(price).length === 0) {
      return { outboundPrice, inboundPrice, totalD2dPrice };
    }

    if (!isEmbeddedPromo && d2d.addBookingCount === 0) {
      if (price.promotionRewardType === 1) { // Fixed
        if (bookingProduct === 'abc') {
          const inboundBooking = state.d2dReducer.inboundBooking.priceReducer.price;
          inboundPrice = d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount - price.discountAppliedMajor;
          // if promo is already applied from timeslot screen
          if (inboundBooking?.promoCodesApplied?.length > 0) {
            inboundPrice = d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount + price.discountAppliedMajor;
          }
        } else {
          const outboundBooking = state.d2dReducer.outboundBooking.priceReducer.price;
          outboundPrice = d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount - price.discountAppliedMajor;
          // if promo is already applied from timeslot screen
          if (outboundBooking?.promoCodesApplied?.length > 0) {
            outboundPrice = d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount + price.discountAppliedMajor;
          }
        }
      }

      if (price.promotionRewardType === 2) { // Percentage
        const discountAmount = price.promotionRewardAmount / 100;

        if (bookingProduct === 'abc') {
          // if promo not already applied from timeslot screen
          const inboundBooking = state.d2dReducer.inboundBooking.priceReducer.price;
          if (typeof inboundBooking?.promoCodesApplied?.length === 'undefined' || inboundBooking?.promoCodesApplied?.length === 0) {
            inboundPrice = d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount - (d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount * discountAmount);
          }
        } else {
          // if promo not already applied from timeslot screen
          const outboundBooking = state.d2dReducer.outboundBooking.priceReducer.price;
          if (typeof outboundBooking?.promoCodesApplied?.length === 'undefined' || outboundBooking?.promoCodesApplied?.length === 0) {
            outboundPrice = d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount - (d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount * discountAmount);
          }
        }
      }
    }

    const milesDiscountedPrice = price.pricingResponse[0].pricingBreakdown.milesDiscountMajor
    if (!milesRedeemed && milesPreview) {
      inboundPrice -= (milesDiscountedPrice / 2);
      outboundPrice -= (milesDiscountedPrice / 2);
    }

    if (milesRedeemed && d2d.addBookingCount === 0) {
      if (bookingProduct === 'abc') {
        const submittedMiles = milesReducer.payByMilesResponse.ccdMilesSubmitted;
        const currencyRatio = milesReducer.miles.currencyRatio;
        const inboundMilesDiscountedPrice = +(submittedMiles / currencyRatio).toFixed(2);
        inboundPrice -= inboundMilesDiscountedPrice;
      } else {
        const submittedMiles = milesReducer.payByMilesResponse.abcMilesSubmitted;
        const currencyRatio = milesReducer.miles.currencyRatio;
        const inboundMilesDiscountedPrice = +(submittedMiles / currencyRatio).toFixed(2);
        outboundPrice -= inboundMilesDiscountedPrice;
      }
    }

    inboundPrice = +(+inboundPrice).toFixed(2);
    outboundPrice = +(+outboundPrice).toFixed(2);
    if (outboundPrice >= 0 && inboundPrice >= 0) {
      totalD2dPrice = inboundPrice + outboundPrice;
    }
    totalD2dPrice = +(+totalD2dPrice).toFixed(2);
    return { outboundPrice, inboundPrice, totalD2dPrice };
  }

  checkFlyBackEligibility() {
    const promise = new Promise((resolve, reject) => {
      const state = this.getState(this._appStore);

      const isD2d = state.bookingReducer.bookingDetails.isD2dService;
      const airlineCode = state.bookingReducer.bookingDetails.carrier.airlineCode;
      const isAbc = state.bookingReducer.bookingDetails.bookingProduct === 'abc';

      const flightDetails = state.flightReducer.flightDetails.flightResponse.flightStatusDetails;

      const departureAirportCode = flightDetails[flightDetails.length - 1].arrivalAirport.airportCode;
      const arrivalAirportCode = flightDetails[0].departureAirport.airportCode;


      let request =  {
        airlineCode: airlineCode,
        departureAirportCode: departureAirportCode,
        arrivalAirportCode: arrivalAirportCode,
        primaryBooking: {
          product: isAbc ? 'ABC' : 'CCD',
          isD2D: false
        }
      }

      if (isD2d) {
        request.primaryBooking.isD2D = isD2d;
      }

      let headers = new HttpHeaders();
      headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
      this._http.post(API_PATH + CHECK_FLY_BACK_ELIGIBILITY, request, { headers: headers })
        .subscribe((res: any) => {
          const returnProducts = res?.flyBackProducts?.map(p => eligibilityProducts[p]) || [];
          this._appStore.dispatch(updateFlybackEligibleProducts({ payload: returnProducts }));
          resolve(res);
        }, err => {
          // console.log(err);
          resolve(err);
        });
    });

    return promise;
  }


  reCheckFlyBackEligibility() {
    const promise = new Promise((resolve, reject) => {
      const oldReducer = this.getState(this._appStore).oldBookingInfoReducer;
      const d2dReducer = oldReducer.d2dReducer;
      const currentBookingProduct = oldReducer.d2dReducer.currentBookingProduct;
      const oldReducerState = currentBookingProduct === "abc" ? d2dReducer.outboundBooking : d2dReducer.inboundBooking;

      const isD2d = oldReducerState.bookingReducer.bookingDetails.isD2dService;
      const airlineCode = oldReducerState.bookingReducer.bookingDetails.carrier.airlineCode;
      const isAbc = oldReducerState.bookingReducer.bookingDetails.bookingProduct === 'abc';

      const flightDetails = oldReducerState.flightReducer.flightDetails.flightResponse.flightStatusDetails;

      const departureAirportCode = flightDetails[flightDetails.length - 1].arrivalAirport.airportCode;
      const arrivalAirportCode = flightDetails[0].departureAirport.airportCode;


      let request =  {
        airlineCode: airlineCode,
        departureAirportCode: departureAirportCode,
        arrivalAirportCode: arrivalAirportCode,
        primaryBooking: {
          product: isAbc ? 'ABC' : 'CCD',
          isD2D: false
        }
      }

      if (isD2d) {
        request.primaryBooking.isD2D = isD2d;
      }

      let headers = new HttpHeaders();
      headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
      this._http.post(API_PATH + CHECK_FLY_BACK_ELIGIBILITY, request, { headers: headers })
        .subscribe((res: any) => {
          const returnProducts = res?.flyBackProducts?.map(p => eligibilityProducts[p]) || [];
          this._appStore.dispatch(updateFlybackEligibleProducts({ payload: returnProducts }));
          resolve(res);
        }, err => {
          // console.log(err);
          resolve(err);
        });
    });

    return promise;
  }

  postPaymentRequest(request, isLoggedIn: Boolean, product: string, type?: string) {
    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    this.updateD2dstate();
    const state = this.getState(this._appStore);
    const flightData = state.flightReducer.flightDetails.flightResponse;
    this.bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
    this.departureCountryCode = flightData.flightStatusDetails[0].departureAirport.airportCountryCode;
    this.arrivalCountryCode = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport.airportCountryCode;
    let modifiedRequest = JSON.parse(JSON.stringify(request))
    // const currency = (this.departureCountryCode === 'CH' && this.bookingProduct === 'abc') || (this.arrivalCountryCode === 'CH' && this.bookingProduct === 'ccd') ? 'CHF' : 'GBP';
    const currency = this.getState(this._appStore).timeslotReducer.currency.code;
    modifiedRequest.totalAmount.currency = currency;
    modifiedRequest.totalAmount.exchangeRate = state.priceReducer.price.totalPrice.exchangeRate;
    modifiedRequest.totalAmount.baseCurrency = ISO_CURRENCY_CODES[state.priceReducer.price.totalPrice.baseCurrency];

    if (state.bookingReducer.bookingDetails.isD2dService && state.d2dReducer.addBookingCount === 1) {
      let upsellBookingReference;
      let upsellBookingAmount;
      let upsellQuoteId;

      if (state.bookingReducer.bookingDetails.bookingProduct === 'abc') {
        const upsellBooking = state.d2dReducer.inboundBooking;
        upsellBookingReference = upsellBooking.bookingReducer.bookingDetails.bookingReference;
        upsellBookingAmount = upsellBooking.priceReducer.price.totalPrice.minorAmount;
        upsellQuoteId = upsellBooking.priceReducer.price.quoteId;
      } else {
        const upsellBooking = state.d2dReducer.outboundBooking;
        upsellBookingReference = upsellBooking.bookingReducer.bookingDetails.bookingReference;
        upsellBookingAmount = upsellBooking.priceReducer.price.totalPrice.minorAmount;
        upsellQuoteId = upsellBooking.priceReducer.price.quoteId;
      }

      if(modifiedRequest.applePayment) {
        modifiedRequest.applePayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.cardDetail = null;
      } else if (modifiedRequest.googlePayment) {
        modifiedRequest.googlePayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.cardDetail = null;
      } else {
        modifiedRequest.cardDetail.upsellBookingAmount = upsellBookingAmount;
      }

      modifiedRequest.upsellQuoteId = upsellQuoteId;
      modifiedRequest.isDoorToDoor = true;
    }

    let headers = new HttpHeaders();
    headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    if (isLoggedIn) {
      headers = this.createAuthorizationHeader(headers, this._token);
    }
    this._makePaymentRequestSubscription = this._http
      .post(API_PATH + MAKE_PAYMENT, modifiedRequest, { headers: headers })
      .subscribe(
        (res: any) => {
          const bookingResponse = res;

          if (type === 'amend') {
            if (bookingResponse.paymentResponse.resultCode === 0 || bookingResponse.paymentResponse.resultCode === 1) {
              // 3DS authorized, complete booking
              this.handleMakePaymentSuccess(product, type);
              this._appStore.dispatch(amendPaymentConfirmed({ confirmed: true }));
              return;
            }
          }
          if (type !== 'amend') {
            const abandonmentRequest = {
              email: this.currentBooking.bookingUser.emailAddress
            };

            this._appStore.dispatch(paymentWithSavedCard({payload: !!modifiedRequest.recurringPayment }));
            this._abandonmentService.setAbandonment(abandonmentRequest, 'Unsubscribe');

            setTimeout(() => {
              this.updateD2dstate();
            }, 30);

          }

            // if (product === 'abc') {
            //   ga('set', 'page', '/abc/complete');
            //   ga('send', 'pageview');
            // } else if (product === 'ccd') {
            //   ga('set', 'page', '/ccd/complete');
            //   ga('send', 'pageview');
            // }
            // this._router.navigate(['complete'], { queryParams: { ref: this.currentBooking.bookingReference } });
            if (bookingResponse.paymentResponse.resultCode === 0 || bookingResponse.paymentResponse.resultCode === 1) {
              // 3DS authorized, complete booking
              this.handleMakePaymentSuccess(product, type);

            } else if (bookingResponse.paymentResponse.resultCode === 7 && bookingResponse.paymentResponse.redirectShopperTokenResponse) {
              this._appLoaderService.hideLoader();
              // 3DS1 Redirection
              const data = {
                serverTransID: '',
                acsTransID: '',
                messageVersion: '',
                acsURL: '',
                paymentSessionId: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentSessionId,
                paymentRequest: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentRequest,
                termUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.termUrl,
                issueUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.issuerUrl,
                notificationUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.notificationUrl,
                resultCode: 7
              };

              this.initiateChallengeShopper(data);

              // this._errorService.handleCustomError({ text: "Unfortunately we can't accept this card please try another card or call customer services. United Kingdom: +44 203 384 6677, Switzerland: +41 800 837 732", sticky: true });
            } else if (bookingResponse.paymentResponse.resultCode === 9) {
              // 3DS Identify Shopper
              const identifyRequest = {
                threeDSServerTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                threeDSMethodNotificationURL: bookingResponse.paymentResponse.tokenResponse.notificationURL
              };
              const encodedIdentifyRequest = ThreedDS2Utils.base64Url.encode(JSON.stringify(identifyRequest));

              const dataObj = {
                request: encodedIdentifyRequest,
                postURL: bookingResponse.paymentResponse.tokenResponse.notificationURL,
                originURL: bookingResponse.paymentResponse.tokenResponse.notificationURLOrigin,
                type: 'identify'
              };

              this._appStore.dispatch(update3dsData({ data: dataObj }));
            } else if (bookingResponse.paymentResponse.resultCode === 10) {
              // 3DS Challenge Shopper
              const data = {
                serverTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                acsTransID: bookingResponse.paymentResponse.tokenResponse.acsTransID,
                messageVersion: bookingResponse.paymentResponse.tokenResponse.messageVersion,
                acsURL: bookingResponse.paymentResponse.tokenResponse.acsURL
              };

              this.initiateChallengeShopper(data);
            }
        },
        err => {
          this.exponeaPaymentErrorEvent(err);
          this.bookingPaymentError$.next(true);
          this._errorService.handleError(err);
          return;
        }
      );

  }

  async postBooking(loggedIn: Boolean, product?: string, applePayToken?: any, googlePayToken?: any) {
    // console.log('add booking api call check token',googlePayToken, applePayToken)
    // // console.log(this.currentBooking, this.getState(this._appStore).flightReducer.flightDetails, 'add booking and pay')
    const state = this.getState(this._appStore);
    const bookingState = JSON.parse(JSON.stringify(state));
    const bookingReducer = bookingState.bookingReducer;
    this.updateD2dstate();
    const ccd = state.flightReducer.flightDetails.flightProducts.ccd;
    const flightDirection = state.flightReducer.flightDetails.flightResponse.flightDirection;
    // const ccd = true;
    // const flightDirection = 2;
    // // console.log(ccd, flightDirection);
    // // console.log(this.currentBooking.bookingJourney[0].addOnProductType);
    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const flightData = state.flightReducer.flightDetails.flightResponse;
    const hasCustomeAgreement = state.bookingReducer.bookingDetails.hasCustomsAgreement;
    this.departureCountryCode = flightData.flightStatusDetails[0].departureAirport.airportCountryCode;
    this.arrivalCountryCode = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport.airportCountryCode;
    this.bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
    // const currency = (this.departureCountryCode === 'CH' && this.bookingProduct === 'abc') || (this.arrivalCountryCode === 'CH' && this.bookingProduct === 'ccd') ? 'CHF' : 'GBP';
    const currency = state.timeslotReducer.currency.code;

    let deliverDateTime;
    let collectionDateTime;
    let modifiedRequest = JSON.parse(JSON.stringify(this.currentBooking));
    modifiedRequest.bookingzoneId = state.timeslotReducer.timeslotFormattedData.bookingZoneId;
    modifiedRequest.bookingJourney[0].isDomesticABC = state.d2dReducer.flightDetails.flightProducts.isDomesticABC;
    modifiedRequest.makePaymentRequest.totalAmount.currency = currency;
    modifiedRequest.makePaymentRequest.totalAmount.exchangeRate = state.priceReducer.price.totalPrice.exchangeRate;
    modifiedRequest.makePaymentRequest.totalAmount.baseCurrency = ISO_CURRENCY_CODES[state.priceReducer.price.totalPrice.baseCurrency];
    modifiedRequest.hasCustomsAgreement = hasCustomeAgreement;
    modifiedRequest.bookingJourney[0].languageCode = state.copyReducer.languageCode;

    if (flightDirection === 1 && state.hubDropReducer.selectedPostOffice && state.hubDropReducer.selectedCollectionType === 'atPostOffice') {
      modifiedRequest.bookingJourney[0].preAcceptanceType = 'HubDrop';
      modifiedRequest.bookingJourney[0].collectionLocation = this.formatCollectionPostLocation(state.hubDropReducer.selectedPostOffice);
      this._appStore.dispatch(isBookingHubdropBooking({ payload: true }));
      this._appStore.dispatch(setSelectedPostOffice({ payload: state.hubDropReducer.selectedPostOffice }));
    }

    if(ccd && flightDirection === 2) {
      const flightLegs = state.flightReducer.flightDetails.flightResponse.flightStatusDetails
      collectionDateTime = flightLegs[flightLegs.length-1].arrivalDateTimeDetails.scheduledDateTimeLocal;
      deliverDateTime = state.timeslotReducer.selectedTimeslotDateTime;

      modifiedRequest.bookingJourney[0].collectionDateTimeUTC = collectionDateTime;
      modifiedRequest.makePaymentRequest.flightDateTime = collectionDateTime;
      modifiedRequest.bookingJourney[0].addOnProductType = [2];

      if (deliverDateTime && !this.currentBooking.bookingJourney[0].deliveryDateTimeUTC) {
        modifiedRequest.bookingJourney[0].deliveryDateTimeUTC = deliverDateTime;
      }
    }

    // modify booking request if d2d Booking
    if (state.bookingReducer.bookingDetails.isD2dService && state.d2dReducer.addBookingCount === 0) {
      modifiedRequest.bookingJourney[0].isDoorToDoorBooking = true;
      // this was already commented out
       //this._appStore.dispatch({ type: 'SAVE_FORM_DATA', payload:  modifiedRequest });
    }
    if (state.bookingReducer.bookingDetails.isD2dService && state.d2dReducer.addBookingCount === 1) {
      let upsellBookingReference;
      let upsellBookingAmount;
      let upsellQuoteId;

      if (state.bookingReducer.bookingDetails.bookingProduct === 'abc') {
        const upsellBooking = state.d2dReducer.inboundBooking;
        upsellBookingReference = upsellBooking.bookingReducer.bookingDetails.bookingReference;
        upsellBookingAmount = upsellBooking.priceReducer.price.totalPrice.minorAmount;
        upsellQuoteId = upsellBooking.priceReducer.price.quoteId;
      } else {
        const upsellBooking = state.d2dReducer.outboundBooking;
        upsellBookingReference = upsellBooking.bookingReducer.bookingDetails.bookingReference;
        upsellBookingAmount = upsellBooking.priceReducer.price.totalPrice.minorAmount;
        upsellQuoteId = upsellBooking.priceReducer.price.quoteId;
      }

      modifiedRequest.bookingJourney[0].upsellBookingReference = upsellBookingReference;
      modifiedRequest.bookingJourney[0].isDoorToDoorBooking = true;

      if(applePayToken) {
        modifiedRequest.makePaymentRequest.applePayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.makePaymentRequest.cardDetail = null;
      } else if (googlePayToken) {
        modifiedRequest.makePaymentRequest.googlePayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.makePaymentRequest.cardDetail = null;
      } else {
        modifiedRequest.makePaymentRequest.cardDetail.upsellBookingAmount = upsellBookingAmount;
      }

      modifiedRequest.makePaymentRequest.upsellQuoteId = upsellQuoteId;
      modifiedRequest.makePaymentRequest.isDoorToDoor = true;
      // console.log({'currentQid': modifiedRequest.makePaymentRequest.quoteId, 'stateQid': state.priceReducer.price.quoteId});
    }

    if (state.utilityReducer.isDiamondAirline) {
      modifiedRequest.isDiamondAirBooking = true;
    }

    if (state.flybackReducer.flybackBooking) {
      const primaryBookingProduct = state.oldBookingInfoReducer.d2dReducer?.currentBookingProduct;
      if (primaryBookingProduct === 'abc') {
        modifiedRequest.bookingJourney[0].flybackLinkedBooking = state.oldBookingInfoReducer.abcBooking?.bookingReducer?.bookingDetails?.bookingReference;
      } else if (primaryBookingProduct === 'ccd') {
        modifiedRequest.bookingJourney[0].flybackLinkedBooking = state.oldBookingInfoReducer.ccdBooking?.bookingReducer?.bookingDetails?.bookingReference;
      }
    } else {
      try {
        const res: any = await this.checkFlyBackEligibility();
        const state = this.getState(this._appStore);
        const d2dReducer = state.d2dReducer;
        this._appStore.dispatch(saveD2DBookingsData({ payload: d2dReducer }));
        this._dataStorageService.saveData();

        if (res.flyBackProducts && res.flyBackProducts.length > 0) {
          modifiedRequest.bookingJourney[0].isEligibleForFlyBack = true;
        } else {
          modifiedRequest.bookingJourney[0].isEligibleForFlyBack = false;
        }
      } catch (e) {
        modifiedRequest.bookingJourney[0].isEligibleForFlyBack = false;
        // console.log(e);
      }
    }

    let headers = new HttpHeaders();
    headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    if (loggedIn) {
      headers = this.createAuthorizationHeader(headers, this._token);
    }

    modifiedRequest.slotNumber = this._sharedService.getCurrentSlotNumber();

    // get departure airport code
    let flightDetails = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
    if (state.flightFinderReducer.selectedMultiLegFlight) {
      flightDetails = state.flightFinderReducer.selectedMultiLegFlight.flightStatusDetails[0];
    }

    // console.log('just before calling api', modifiedRequest);

    this._postBookingSubscription = this._http
      .post(API_PATH + ADD_BOOKING, modifiedRequest, { headers: headers })
      .subscribe(
        (res: any) => {
          const bookingResponse = res;

          if (res.bookingReference && res.bookingJourneyReference) {
            this.completeBookingRef = res.bookingReference;
            this.saveNewBookingReference(bookingResponse);
          }

          // incomplete booking
          if (bookingResponse.validationErrorMessages !== null) {
            this._appStore.dispatch(updateAddBookingCalled({ payload: true }));
            return;
          } else if (bookingResponse.validationErrorMessages === null && bookingResponse.paymentResponse === null) {
            // this.upsellBookingAdded$.next(true);
            this._appStore.dispatch(paymentWithSavedCard({payload: !!modifiedRequest.makePaymentRequest.recurringPayment }));
            this._appStore.dispatch(updateReturnCustomerUrl({ payload: bookingResponse.returnJourneyURL }));
            this._appStore.dispatch(updatePnrData({ payload: bookingResponse.pnrBookingData }));
            this._appStore.dispatch(updateAddBookingCount({ payload: 1 }));

            setTimeout(() => {
              this.updateD2dstate();
              const bookingProduct = bookingReducer.bookingDetails.bookingProduct !== 'abc' ? 'abc' : 'ccd';
              this._eventsService.goToBookingSwitcher(bookingProduct, 'payment');
            }, 30);

          } else {

            // complete booking
            if (product === 'abc') {
              ga('set', 'page', '/abc/complete');
              ga('send', 'pageview');
            } else if (product === 'ccd') {
              ga('set', 'page', '/ccd/complete');
              ga('send', 'pageview');
            }

            // this.loginNewUser();

            // const abandonmentRequest = {
            //   email: this.currentBooking.bookingUser.emailAddress
            // };

            // this._abandonmentService.setAbandonment(abandonmentRequest, 'Unsubscribe');
            this.bookingProduct = product;
            this._appStore.dispatch(paymentWithSavedCard({payload: !!modifiedRequest.makePaymentRequest.recurringPayment }));
            this._appStore.dispatch(updateReturnCustomerUrl({ payload: bookingResponse.returnJourneyURL }));
            this._appStore.dispatch(updatePnrData({ payload: bookingResponse.pnrBookingData }));
            setTimeout(() => {
              this.updateD2dstate();

              if (bookingResponse.paymentResponse.resultCode === 0 || bookingResponse.paymentResponse.resultCode === 1) {
                // 3DS authorized, complete booking
                this.handleBookingSuccess();

              } else if (bookingResponse.paymentResponse.resultCode === 7) {
                // this._appLoaderService.hideLoader();
                // this._errorService.handleCustomError({ text: "Unfortunately we can't accept this card please try another card or call customer services. United Kingdom: +44 203 384 6677, Switzerland: +41 800 837 732", sticky: true });
                 // 3DS1 Redirection
                 const data = {
                  serverTransID: '',
                  acsTransID: '',
                  messageVersion: '',
                  acsURL: '',
                  paymentSessionId: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentSessionId,
                  paymentRequest: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentRequest,
                  termUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.termUrl,
                  issueUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.issuerUrl,
                  notificationUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.notificationUrl,
                  resultCode: 7
                };

                this.initiateChallengeShopper(data);
              } else if (bookingResponse.paymentResponse.resultCode === 9) {
                // 3DS Identify Shopper
                const identifyRequest = {
                  threeDSServerTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                  threeDSMethodNotificationURL: bookingResponse.paymentResponse.tokenResponse.notificationURL
                };
                const encodedIdentifyRequest = ThreedDS2Utils.base64Url.encode(JSON.stringify(identifyRequest));

                const dataObj = {
                  request: encodedIdentifyRequest,
                  postURL: bookingResponse.paymentResponse.tokenResponse.notificationURL,
                  originURL: bookingResponse.paymentResponse.tokenResponse.notificationURLOrigin,
                  type: 'identify'
                };

                this._appStore.dispatch(update3dsData({ data: dataObj }));
              } else if (bookingResponse.paymentResponse.resultCode === 10) {
                // 3DS Challenge Shopper
                const data = {
                  serverTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                  acsTransID: bookingResponse.paymentResponse.tokenResponse.acsTransID,
                  messageVersion: bookingResponse.paymentResponse.tokenResponse.messageVersion,
                  acsURL: bookingResponse.paymentResponse.tokenResponse.acsURL
                };

                this.initiateChallengeShopper(data);
              }

            }, 30);


            // this._router.navigate(['complete'], { queryParams: { ref: this.currentBooking.bookingReference } });
          }
        },
        err => {
          let errJson = null;

          if (err.error.bookingReference && err.error.bookingJourneyReference) {
            this.completeBookingRef = err.error.bookingReference;
            this.saveNewBookingReference(err.error);
          }
          // console.log(err);
          try {
            errJson = err;
          } catch (cErr) {
            errJson = JSON.parse(err._body);
          }

          if (typeof errJson.returnJourneyURL !== 'undefined') {
            this._appStore.dispatch(updateReturnCustomerUrl({ payload: errJson.returnJourneyURL }));
          }

          this.exponeaPaymentErrorEvent(err);
          this.bookingPaymentError$.next(true);
          this._errorService.handleError(err);

          this.loginNewUser();

          return;
        }
      );

  }

  initiateChallengeShopper(data) {

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });

    if (data.resultCode && data.resultCode === 7) {
      const dataObj = {
        request: '',
        postURL: '',
        originURL: '',
        type: 'redirect',
        paymentSessionId: data.paymentSessionId,
        paymentRequest: data.paymentRequest,
        termUrl: data.termUrl,
        issueUrl: data.issueUrl,
        notificationUrl: data.notificationUrl,
      };
      this._appStore.dispatch(update3dsData({ data: dataObj }));
    } else {
      // 3DS Challenge shopper
      const challengeRequest = {
        threeDSServerTransID: data.serverTransID,
        acsTransID: data.acsTransID,
        messageVersion: data.messageVersion,
        messageType: 'CReq',
        challengeWindowSize: '05'
      };

      const encodedChallengeRequest = ThreedDS2Utils.base64Url.encode(JSON.stringify(challengeRequest));

      const dataObj = {
        request: encodedChallengeRequest,
        postURL: data.acsURL,
        originURL: data.originURL,
        type: 'challenge'
      };
      this._appStore.dispatch(update3dsData({ data: dataObj }));
    }
  }

  handleBookingSuccess() {
    if (this.bookingProduct === 'abc') {
      ga('set', 'page', '/abc/complete');
      ga('send', 'pageview');
    } else if (this.bookingProduct === 'ccd') {
      ga('set', 'page', '/ccd/complete');
      ga('send', 'pageview');
    }

    this.loginNewUser();

    const abandonmentRequest = {
      email: this.currentBooking.bookingUser.emailAddress
    };

    this.handlePaymentSuccess();

    this._abandonmentService.setAbandonment(abandonmentRequest, 'Unsubscribe');
    const isFlyback = this.getState(this._appStore).flybackReducer.flybackBooking;
    if (isFlyback) {
      this._appStore.dispatch(updateFlyBackCompleteStatus({payload: true }));
      this._appStore.dispatch(updateFlybackBookingStatus({ payload: false }));
      setTimeout(() => {
        this._router.navigate(['complete'], { queryParams: { ref: this.completeBookingRef || this.currentBooking.bookingReference }});
      }, 2500);
    } else {
      // check for flyback eligibility
      this.checkFlyBackEligibility().then((res: any) => {
        const state = this.getState(this._appStore);
        const d2dReducer = state.d2dReducer;
        this._appStore.dispatch(saveD2DBookingsData({ payload: d2dReducer }));
        this._dataStorageService.saveData();

        if (res.flyBackProducts && res.flyBackProducts.length > 0) {
          this._router.navigate(['fly-back-details'], { queryParams: { ref: this.completeBookingRef } });
        } else {
          setTimeout(() => {
            this._router.navigate(['complete'], { queryParams: { ref: this.completeBookingRef || this.currentBooking.bookingReference }});
          }, 2500);
        }
      });
    }

  }

  handleMakePaymentSuccess(product, type) {

    this.handlePaymentSuccess();

    if (type === 'amend') {
      const messageData = {
        icon: 'tick_in_circle',
        sticky: false,
        type: 'success',
        text: 'Booking paid. You will be re-directed back to your account.'
      };

      this._errorService.handleCustomInfo(messageData);

      setTimeout(() => {
        this._router.navigate(['my-account/bookings/booking-details'], { queryParams: { ref: this.currentBooking.bookingReference }});
      }, 2500);
      return;

    } else {
      const abandonmentRequest = {
        email: this.currentBooking.bookingUser.emailAddress
      };

      this._abandonmentService.setAbandonment(abandonmentRequest, 'Unsubscribe');

      if (product === 'abc') {
        ga('set', 'page', '/abc/complete');
        ga('send', 'pageview');
      } else if (product === 'ccd') {
        ga('set', 'page', '/ccd/complete');
        ga('send', 'pageview');
      } else if (product === 'sds') {
        ga('set', 'page', '/sds/complete');
        ga('send', 'pageview');
      }

      const isFlyback = this.getState(this._appStore).flybackReducer.flybackBooking;
      if (isFlyback) {
        this._appStore.dispatch(updateFlybackBookingStatus({ payload: false }));
        this._appStore.dispatch(updateFlyBackCompleteStatus({payload: true }));
        setTimeout(() => {
          this._router.navigate(['complete'], { queryParams: { ref: this.completeBookingRef || this.currentBooking.bookingReference } });
        }, 2500);
      } else {
        this.checkFlyBackEligibility().then((res: any) => {
          const state = this.getState(this._appStore);
          const d2dReducer = state.d2dReducer;
          this._appStore.dispatch(saveD2DBookingsData({ payload: d2dReducer }));
          this._dataStorageService.saveData();

          if (res.flyBackProducts && res.flyBackProducts.length > 0) {
            this._router.navigate(['fly-back-details'], { queryParams: { ref: this.completeBookingRef } });
          } else {
            this._router.navigate(['complete'], { queryParams: { ref: this.completeBookingRef || this.currentBooking.bookingReference }});
          }
        });
      }
    }

    // this code was already commented
    // if (bookingResponse.validationErrorMessages !== null) {
    //   // console.log(bookingResponse.validationErrorMessages);
    // } else {
    //this._appStore.dispatch({ type: 'UPDATE_BOOKING_PAID', payload: true });
    //   this._router.navigate(['complete']);
    // }
  }

  getAmendBookingFlagValue(newFlag) {
    this._amendBookingFlag = newFlag;
    return this._amendBookingFlag;
  }

  formatCollectionPostLocation(data) {
    return {
      "type": 4,
      "name": data.name,
      "shortName": "",
      "geoCoord": data.geoCoord,
      "addressLine1": data.addressLine1,
      "addressLine2": data.addressLine2,
      "addressLine3": "",
      "addressTown": data.addressTown,
      "addressPostCode": data.addressPostCode,
      "addressCounty": data.addressCounty,
      "addressCountry": data.addressCountry
    };
  }

  // amends
  postAmend(data, storeData?) {

    const amendFlag = this.getAmendBookingFlagValue(data.flag);

    const request: AmendRequest = {
      amendBookingFlag: amendFlag,
      bookingJourneyReference: data.reference,
      addBookingRequest: this.currentBooking
    };

    if (storeData) {
      this._appStore.dispatch(updateAmendObject({ payload: request }));
      return;
    }

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    let headers = new HttpHeaders();
    headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    headers = this.createAuthorizationHeader(headers, this._token);

    const state = this.getState(this._appStore);
    const flightData = state.flightReducer.flightDetails.flightResponse;
    const ccd = state.bookingReducer.bookingDetails.bookingType === 'amend' ? state.bookingReducer.bookingDetails.bookingProduct : state.flightReducer.flightDetails.flightProducts.ccd;
    const flightDirection = state.flightReducer.flightDetails.flightResponse.flightDirection;
    const majorPriceDifference = state.priceReducer.price.priceHistory.majorPriceDifference;

    const booking_user = state.currentBookingReducer.currentBooking.bookingUser;
    const cs_user = state.userReducer.userDetails;

    const is_cs = cs_user.userRole !== "CUSTOMER";

    let amendmentFlag = AMEND_MAPPING[amendFlag];

    let amendExponeaEventName = 'amend_summary';

    if (amendFlag === 64) {
      amendExponeaEventName = 'amend_customer_details';
      const bookerDataBackup = state.bookingReducer.bookingDetails.bookerDataBackup;

      if(is_cs && bookerDataBackup.emailAddress !== booking_user.emailAddress){
        amendmentFlag = `email`;
      }

      if(bookerDataBackup.firstName !== booking_user.firstName){
        amendmentFlag = amendmentFlag ? `${amendmentFlag}, first name` : 'first name';
      }

      if(bookerDataBackup.lastName !== booking_user.lastName){
        amendmentFlag = amendmentFlag ? `${amendmentFlag}, last name` : 'last name';
      }

      if(bookerDataBackup.phone !== booking_user.phoneNumber || bookerDataBackup.countryCode !== booking_user.countryCode){
        amendmentFlag = amendmentFlag ? `${amendmentFlag}, phone` : 'phone';
      }

    }

    // const ccd = true;
    let deliverDateTime;
    let collectionDateTime;
    let modifiedRequest = JSON.parse(JSON.stringify(request))
    if (ccd && flightDirection === 2) {
      const flightLegs = state.flightReducer.flightDetails.flightResponse.flightStatusDetails;
      collectionDateTime = flightLegs[flightLegs.length-1].arrivalDateTimeDetails.scheduledDateTimeLocal;
      deliverDateTime = state.timeslotReducer.selectedTimeslotDateTime;
      if(deliverDateTime === null || deliverDateTime === '') {
        deliverDateTime = state.currentBookingReducer.currentBooking.bookingJourney[0].deliveryDateTimeUTC;
      }
      let bookingJourney = {...modifiedRequest.addBookingRequest.bookingJourney[0]}
      const updatedBookingJourney = { ...bookingJourney, collectionDateTimeUTC: collectionDateTime, deliveryDateTimeUTC: deliverDateTime};
      modifiedRequest = {
        ...modifiedRequest,
        addBookingRequest: {
          ...modifiedRequest.addBookingRequest,
          bookingJourney: [updatedBookingJourney]
        }
      };

      // modifiedRequest.makePaymentRequest.flightDateTime = collectionDateTime;
      // modifiedRequest.addBookingRequest.bookingJourney[0].addOnProductType = [2];
      // if (deliverDateTime && !this.currentBooking.bookingJourney[0].deliveryDateTimeUTC) {
      //   modifiedRequest.bookingJourney[0].deliveryDateTimeUTC = deliverDateTime;
      // }
    }
    this.departureCountryCode = flightData.flightStatusDetails[0].departureAirport.airportCountryCode;
    this.arrivalCountryCode = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport.airportCountryCode;
    this.bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
    // const currency = (this.departureCountryCode === 'CH' && this.bookingProduct === 'abc') || (this.arrivalCountryCode === 'CH' && this.bookingProduct === 'ccd') ? 'CHF' : 'GBP';
    const currency = this.getState(this._appStore).timeslotReducer.currency.code;

    modifiedRequest.addBookingRequest.makePaymentRequest.totalAmount.currency = currency;
    modifiedRequest.addBookingRequest.bookingZoneId = state.timeslotReducer.timeslotFormattedData ? state.timeslotReducer.timeslotFormattedData.bookingZoneId : '';
    // check if hubdrop
    if (flightDirection === 1 && state.hubDropReducer.selectedPostOffice && state.hubDropReducer.selectedCollectionType === 'atPostOffice') {
      modifiedRequest.bookingJourney[0].preAcceptanceType = 'HubDrop';
      modifiedRequest.makePayment.shopperInfo.billingAddress = modifiedRequest.bookingJourney[0].collectionLocation;
      modifiedRequest.bookingJourney[0].collectionLocation = this.formatCollectionPostLocation(state.hubDropReducer.selectedPostOffice);
    }
    // if flight amended then send these attr into exponea
    let requestDepartureDate = '';
    let requestFlightNumber = '';
    if(state.bookingReducer.bookingDetails.bookingSelectionType) {
      requestDepartureDate = moment(modifiedRequest.addBookingRequest.bookingJourney[0].flightData.requestDepartureDate).format('DD/MM/YYYY');
      requestFlightNumber = modifiedRequest.addBookingRequest.bookingJourney[0].flightData.requestFlightNumber;

    }
    this._appLoaderService.showLoader('Booking reference: ' + modifiedRequest.addBookingRequest.bookingReference)

    // if(modifiedRequest.addBookingRequest.makePaymentRequest.totalAmount.amount === 0){
    //   modifiedRequest.addBookingRequest.makePaymentRequest = null;
    // }

    modifiedRequest.slotNumber = this._sharedService.getCurrentSlotNumber();;

    this._http
      .post(API_PATH + AMEND_BOOKING, modifiedRequest, { headers: headers })
      .subscribe(
        (res: any) => {
          this._appLoaderService.hideLoader();
          const isCustomer = state.userReducer.userDetails.userRole === 'CUSTOMER';
          if (!res.paymentResponse) {
            this.handleAmendSuccess();
            const eventAttributes: any = {
              email: booking_user.emailAddress,
              cs_amended: is_cs,
              cs_name: is_cs ? `${cs_user.currentUser.firstName} ${cs_user.currentUser.lastName}` : '',
              cs_email: is_cs ? cs_user.currentUser.emailAddress : '',
              amendment_flag: amendmentFlag,
              amend_failed: 'No',
              fail_reason: '',
              date: requestDepartureDate,
              flight_number: requestFlightNumber,
              phone_number: '+' + booking_user.countryCode + booking_user.phoneNumber
            };
            exponea.track(amendExponeaEventName, eventAttributes);
          } else {
            const bookingResponse = res;

            if (bookingResponse.paymentResponse.resultCode === 0 || bookingResponse.paymentResponse.resultCode === 1) {
              // 3DS authorized, complete booking
              this.handleAmendSuccess();
              const eventAttributes: any = {
                email: booking_user.emailAddress,
                cs_amended: is_cs,
                cs_name: is_cs ? `${cs_user.currentUser.firstName} ${cs_user.currentUser.lastName}` : '',
                cs_email: is_cs ? cs_user.currentUser.emailAddress : '',
                amendment_flag: amendmentFlag,
                amend_failed: 'No',
                fail_reason: '',
                date: requestDepartureDate,
                flight_number: requestFlightNumber,
                phone_number: '+' + booking_user.countryCode + booking_user.phoneNumber
              };
              exponea.track(amendExponeaEventName, eventAttributes);

            } else if (bookingResponse.paymentResponse.resultCode === 7) {
              // this._appLoaderService.hideLoader();
              // this._errorService.handleCustomError({ text: "Unfortunately we can't accept this card please try another card or call customer services. United Kingdom: +44 203 384 6677, Switzerland: +41 800 837 732", sticky: true });

               // 3DS1 Redirection
               const data = {
                serverTransID: '',
                acsTransID: '',
                messageVersion: '',
                acsURL: '',
                paymentSessionId: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentSessionId,
                paymentRequest: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentRequest,
                termUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.termUrl,
                issueUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.issuerUrl,
                notificationUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.notificationUrl,
                resultCode: 7
              };

              this.initiateChallengeShopper(data);

              // exponea.track(amendExponeaEventName, {
              //   email : booking_user.emailAddress,
              //   cs_amended : is_cs,
              //   cs_name : is_cs ? `${cs_user.currentUser.firstName} ${cs_user.currentUser.lastName}`: '',
              //   cs_email : is_cs ? cs_user.currentUser.emailAddress : '',
              //   amendment_flag: amendmentFlag,
              //   amend_failed: 'Yes',
              //   fail_reason: "Unfortunately we can't accept this card please try another card or call customer services. United Kingdom: +44 203 384 6677, Switzerland: +41 800 837 732"
              // });

            } else if (bookingResponse.paymentResponse.resultCode === 9) {
              // 3DS Identify Shopper
              const identifyRequest = {
                threeDSServerTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                threeDSMethodNotificationURL: bookingResponse.paymentResponse.tokenResponse.notificationURL
              };
              const encodedIdentifyRequest = ThreedDS2Utils.base64Url.encode(JSON.stringify(identifyRequest));

              const dataObj = {
                request: encodedIdentifyRequest,
                postURL: bookingResponse.paymentResponse.tokenResponse.notificationURL,
                originURL: bookingResponse.paymentResponse.tokenResponse.notificationURLOrigin,
                type: 'identify'
              };
              this._appStore.dispatch(update3dsData({ data: dataObj }));
            } else if (bookingResponse.paymentResponse.resultCode === 10) {
              // 3DS Challenge Shopper
              const data = {
                serverTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                acsTransID: bookingResponse.paymentResponse.tokenResponse.acsTransID,
                messageVersion: bookingResponse.paymentResponse.tokenResponse.messageVersion,
                acsURL: bookingResponse.paymentResponse.tokenResponse.acsURL
              };

              this.initiateChallengeShopper(data);
            }
          }
        },
        err => {
          // if payment fails on amend
          // set flag to call makePayment on next try
          this._appLoaderService.hideLoader();
          if (err.error.paymentResponse !== null) {
            if (!err.error.paymentResponse.isSuccessful) {
              this._appStore.dispatch(updateBookingAmendPaymentFailed({ payload: true }));
            }
          }
          this._errorService.handleError(err);
          const eventAttributes: any = {
            email: booking_user.emailAddress,
            cs_amended: is_cs,
            cs_name: is_cs ? `${cs_user.currentUser.firstName} ${cs_user.currentUser.lastName}` : '',
            cs_email: is_cs ? cs_user.currentUser.emailAddress : '',
            amendment_flag: amendmentFlag,
            amend_failed: 'Yes',
            fail_reason: JSON.stringify(err),
            date: requestDepartureDate,
            flight_number: requestFlightNumber,
            phone_number: '+' + booking_user.countryCode + booking_user.phoneNumber
          };

          exponea.track(amendExponeaEventName, eventAttributes);

          return;
        });

  }

  handleAmendSuccess() {
    const messageData = {
      icon: 'alert',
      sticky: false,
      type: 'info',
      text: 'Your booking has been saved. You will be re-directed back to booking details'
    };

    this._errorService.handleCustomInfo(messageData);

    this.handlePaymentSuccess();

    setTimeout(() => {
      this._router.navigate(['my-account/bookings/booking-details'], { queryParams: { ref: this.currentBooking.bookingReference }});
    }, 2500);
  }

  handlePaymentSuccess () {
    const d2dReducer = this.getState(this._appStore).d2dReducer;
    const applePayData = this.getApplePaymentToken();

    // console.log('Payment successfull', applePayData , d2dReducer);

    if(applePayData && d2dReducer.paymentType === 1){
      this.applePaymentComplete$.next(true);
    }

    // TODO: for GPay if needed
  }

  loginNewUser() {

    const newUser = this.getState(this._appStore).userReducer.userDetails.newUser;

    if (newUser !== null) {
      this._loginService.setPassword(newUser.emailAddress, newUser.password);
    }
  }

  exponeaPaymentErrorEvent(err) {
    let errJson = null;
    const sectionCode = [];
    let messageTextHolder = '';
    const state = this.getState(this._appStore);
    let payment_type = 'Card';
    if (state.d2dReducer.paymentType === 1 && state.d2dReducer.applePayToken) {
      payment_type = 'Apple Pay';
    } else if (state.d2dReducer.paymentType === 0 && state.d2dReducer.googlePayToken) {
      payment_type = 'Google Pay';
    }
    const saved_card = payment_type === 'Card' ? state.d2dReducer.paymentWithSavedCard : false;
    let card_type = payment_type === 'Card' ? state.d2dReducer.cardType : '';
    const logged_in = state.userReducer.userDetails.userLoggedIn;
    const email = state.userReducer.userDetails.userEmail;

    const promo_type = state.d2dReducer.promoDetails.promotType;

      let promo_amount = state.d2dReducer.promoDetails.promoAmount;
      if (promo_type && promo_type === 2 && promo_amount === 100 && !saved_card) {
        card_type = '';
        payment_type = '';
      }

    try {
      errJson = err.error;
    } catch (cErr) {
      errJson = JSON.parse(err._body);
    }

    if (err.status >= 400 && err.status < 500) {
      if (errJson.validationErrorMessages && typeof errJson.validationErrorMessages !== 'undefined') {
        for (const item of errJson.validationErrorMessages) {
          const sectionReason = item.reasons;
          sectionCode.push(item.code);
          for (const reason of sectionReason) {
            messageTextHolder += reason + '<br>';
          }
        }

        if (errJson.translationResponse?.translationValidationErrors?.length > 0) {
          messageTextHolder = '';
        }
        sectionCode.push(errJson.translationResponse.code)
        for (const err of errJson.translationResponse?.translationValidationErrors) {
          messageTextHolder += err.errorMessage + '<br>';
          const changeAbles = Object.keys(err.parameters);
          if (changeAbles.length > 0) {
            for (const params of changeAbles) {
              const text = '{'+params+'}';
              const params_text = err.parameters[params];
              messageTextHolder = messageTextHolder.replace(text, params_text)
            }
          }
        }

      } else if (typeof errJson.validationErrors !== 'undefined' && typeof errJson.translationResponse !== 'undefined') {
        for (const err of errJson.validationErrors) {
          messageTextHolder += err.errorMessage + '<br>';
        }

      } else if (typeof errJson.modelState !== 'undefined') {
        const model = errJson.modelState,
        modelCode = [],
        modelText = [];

        for (let h = 0; h < Object.keys(model).length; h++) {
          const prop = model[Object.keys(model)[h]];
          // split string on :
          const code = prop[0].split(':');
          // parse and push into array
          modelCode.push(parseInt(code[0], 10));
          modelText.push(code[1]);
        }
        if (typeof modelText[0] !== 'undefined') {
          messageTextHolder = modelText[0];
        } else {
          messageTextHolder = 'There seems to have been an issue. If the problem persists please email: <a href="mailto:support@portr.com">support@portr.com</a>';
          if (errJson.message) {
            messageTextHolder = errJson.message;
          }
        }

      } else if (typeof errJson.error_description !== 'undefined') {
        messageTextHolder = errJson.error_description

      } else if (typeof errJson.validationErrors !== 'undefined') {
        for (const item of errJson.validationErrors) {
          messageTextHolder = item;
        }

      } else if (typeof errJson.description !== 'undefined') {
        messageTextHolder = errJson.description;
      } else {
        messageTextHolder = 'There seems to have been an issue. If the problem persists please email: <a href="mailto:support@portr.com">support@portr.com</a>';
      }

      const bookingDetails = state.bookingReducer.bookingDetails;
      const currency = state.timeslotReducer.currency;
      exponea.track('payment_error', {
        booking_reference: bookingDetails.bookingReference,
        currency: currency.code,
        item_value_gross: bookingDetails.totalPrice,
        reason_forerror: messageTextHolder,
        payment_type,
        card_type,
        saved_card,
        logged_in,
        email
      });
    } else if(err.status === 500) {
      const bookingDetails = state.bookingReducer.bookingDetails;
        const currency = state.timeslotReducer.currency;
        exponea.track('payment_error', {
          booking_reference: bookingDetails.bookingReference,
          currency: currency.code,
          item_value_gross: bookingDetails.totalPrice,
          reason_forerror: '500 error',
          payment_type,
          card_type,
          saved_card,
          logged_in,
          email
        });
    }
  }


  // apple pay

  validateApplePayMerchant (url: string, callback: (value: Object) => void) {

    const headers = new HttpHeaders()
    .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._http
    .post(API_PATH + GET_APPLE_PAY_SESSION, {validationURL: url}, { headers: headers })
    .subscribe(
      (res) => callback(res),
      err => {
        this._errorService.handleError(err);
        return;
      });
  }

  saveNewBookingReference(bookingResponse) {
    this.updateBookingReference(bookingResponse.bookingReference);
    this.updateBookingJourneyReference(bookingResponse.bookingJourneyReference);
    this._appStore.dispatch(updateBookingReference({payload: bookingResponse.bookingReference}));
    this._appStore.dispatch(updateBookingJourneyReference({payload: bookingResponse.bookingJourneyReference}));
  }

  resetForFlyback() {
    this.currentBooking = initialBooking;
    this._bookingOriginal = Object.assign({}, this.currentBooking);

    this._currentBookingJourney = initialBookingJourney;
    this._bookingJourneyOriginal = Object.assign({}, this._currentBookingJourney);

    this._currentBookingFlightData = initialBookingFlightData;
    this._bookingFlightOriginal = Object.assign({}, this._currentBookingFlightData);

    this._currentBookingDeliveryLocation = initialDeliveryLocation;
    this._bookingDeliveryLocationOriginal = Object.assign({}, this._currentBookingDeliveryLocation);

    this._currentBookingCollectionLocation = initialCollectionLocation;
    this._bookingCollectionLocationOriginal = Object.assign({}, this._currentBookingCollectionLocation);

    this._currentBookingMakePayment = initialMakePaymentRequest;
    this._bookingMakePaymentOriginal = Object.assign({}, this._currentBookingMakePayment);
  }

};
